import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SearchResult} from '@github-ui/taillights-worker/document'
import {startTransition, useCallback, useEffect, useEffectEvent, useReducer, useRef} from 'react'

import {sourceSearchReducer} from './source-navigation-state'

export function useSourceSearch(
  kind: 'text' | 'symbol',
  onResult: (result: SearchResult, focus: boolean) => void,
  onError: (error: Error) => void,
) {
  const controller = useTaillightsController()
  const [state, dispatch] = useReducer(sourceSearchReducer, {
    query: '',
    index: 0,
    revision: 0,
    focus: false,
    result: null,
  })
  const reportResult = useEffectEvent(onResult)
  const reportError = useEffectEvent((error: Error, failedRevision: number) => {
    if (state.revision === failedRevision) onError(error)
  })
  const {query, index, revision, result} = state
  // Invalidate late callbacks before React commits the urgent query update.
  const intentRevisionRef = useRef(0)
  const reportedRevisionRef = useRef(-1)
  useEffect(() => {
    if (!controller || !query || intentRevisionRef.current !== revision) return
    let active = true
    async function search() {
      if (!controller) return
      try {
        const value = await controller.search({query, kind, index})
        if (active && intentRevisionRef.current === revision) {
          startTransition(() => dispatch({type: 'result', revision, result: value}))
        }
      } catch (error) {
        if (active && intentRevisionRef.current === revision) {
          reportError(error instanceof Error ? error : new Error(String(error)), revision)
        }
      }
    }
    void search()
    return () => {
      active = false
    }
  }, [controller, kind, query, index, revision])
  useEffect(() => {
    if (result && intentRevisionRef.current === revision && reportedRevisionRef.current !== revision) {
      reportedRevisionRef.current = revision
      reportResult(result, state.focus)
    }
  }, [result, revision, state.focus])
  const changeQuery = useCallback((value: string) => {
    dispatch({type: 'query', query: value, revision: ++intentRevisionRef.current})
  }, [])
  const move = useCallback(
    (delta: number) => {
      dispatch({type: 'move', delta, query, index, revision: ++intentRevisionRef.current})
    },
    [query, index],
  )
  const isCurrent = useCallback((requestedRevision: number) => intentRevisionRef.current === requestedRevision, [])
  return {
    query,
    result,
    revision,
    isCurrent,
    changeQuery,
    move,
  }
}
